import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { authenticateUser } from '@/react/portainer/generated-api/portainer/sdk.gen';
import { usePublicSettings } from '@/react/portainer/settings/queries';
import { AuthenticationMethod } from '@/react/portainer/settings/types';
import { notifyError } from '@/portainer/services/notifications';
import { captureReturnUrlFromQuery } from '@/react/portainer/helpers/returnUrl';

import { InlineLoader } from '@@/InlineLoader';
import { Button } from '@@/buttons';
import { Alert } from '@@/Alert';

import { LoginPanel } from './LoginPanel';
import { Container } from './Container';
import { Logo } from './Logo';
import { AuthCard } from './AuthCard';
import { resolveLicenseError } from './authErrors';
import { useOAuthCallback, useOAuthUri } from './useOAuthCallback';
import { useLogoutReason } from './useLogoutReason';

const title = (
  <div className="p-5 text-center">
    <p className="text-xl">Log in to your account</p>
    <p className="text-md text-muted">
      Welcome back! Please enter your details
    </p>
  </div>
);

export function LoginView({
  onLoginSuccess,
}: {
  onLoginSuccess(): Promise<void>;
}) {
  const settingsQuery = usePublicSettings();

  useEffect(() => {
    captureReturnUrlFromQuery();
  }, []);

  const oauthCallback = useOAuthCallback();
  const logoutReason = useLogoutReason();
  const loginMutation = useLoginMutation({ onSuccess: onLoginSuccess });

  // Cached so both survive the panel remount on a failed login.
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
  });
  const [standardLoginVisibleOverride, setStandardLoginVisibleOverride] =
    useState<boolean>();

  const authError = loginMutation.error
    ? resolveLicenseError(loginMutation.error, 'Unable to login')
    : oauthCallback.authError || logoutReason || '';
  const loginInProgress = oauthCallback.isProcessing || loginMutation.isLoading;

  const settings = settingsQuery.data;

  const oauthUri = useOAuthUri(settings?.OAuthLoginURI, loginInProgress);

  const autoRedirectingToOAuth =
    !!settings?.OAuthHideInternalAuth && !loginInProgress && !authError;

  useEffect(() => {
    if (autoRedirectingToOAuth && oauthUri) {
      window.location.assign(oauthUri);
    }
  }, [autoRedirectingToOAuth, oauthUri]);

  function onSubmit(values: { username: string; password: string }) {
    setCredentials(values);
    loginMutation.mutate(values);
  }

  if (settingsQuery.isError) {
    return (
      <Container>
        <p className="p-5 text-center text-xl">Log in to your account</p>
        <AuthCard>
          <Alert color="error">Unable to load the login page.</Alert>
          <Button
            className="mt-3 w-full"
            data-cy="auth-retryButton"
            onClick={() => settingsQuery.refetch()}
          >
            Retry
          </Button>
        </AuthCard>
      </Container>
    );
  }

  if (loginInProgress || !settings || autoRedirectingToOAuth) {
    return (
      <Container>
        {title}
        <AuthCard>
          <InlineLoader size="xs" className="justify-center">
            Authentication in progress...
          </InlineLoader>
        </AuthCard>
      </Container>
    );
  }

  return (
    <Container>
      <div className="flex justify-center">
        <Logo logo={settings.LogoURL} />
      </div>
      {title}
      <LoginPanel
        onSubmit={onSubmit}
        initialValues={credentials}
        authError={authError}
        customBanner={settings.CustomLoginBanner}
        oauthLoginUri={oauthUri}
        oauthProvider={determineOauthProvider(settings.OAuthLoginURI)}
        method={settings.AuthenticationMethod}
        isStandardLoginVisible={
          standardLoginVisibleOverride ??
          settings.AuthenticationMethod !== AuthenticationMethod.OAuth
        }
        onToggleStandardLogin={() =>
          setStandardLoginVisibleOverride(
            (visible) =>
              !(
                visible ??
                settings.AuthenticationMethod !== AuthenticationMethod.OAuth
              )
          )
        }
      />
    </Container>
  );
}

function determineOauthProvider(loginUri: string) {
  if (loginUri.includes('login.microsoftonline.com')) return 'Microsoft';
  if (loginUri.includes('accounts.google.com')) return 'Google';
  if (loginUri.includes('github.com')) return 'Github';
  return 'OAuth';
}

function useLoginMutation({ onSuccess }: { onSuccess(): void }) {
  return useMutation({
    mutationFn: ({
      username,
      password,
    }: {
      username: string;
      password: string;
    }) => authenticateUser({ body: { apiKey: '', username, password } }),
    onSuccess: onSuccess,
    onError: (err: unknown) => {
      notifyError('Failure', err, resolveLicenseError(err, 'Unable to login'));
    },
  });
}
